import { useEffect, useRef, useState, useCallback } from 'react';
import { type LocalAudioTrack, type RemoteAudioTrack } from 'livekit-client';
import gsap from 'gsap';
import {
  type AgentState,
  type TrackReference,
  type TrackReferenceOrPlaceholder,
  useTrackVolume,
} from '@livekit/components-react';

const DEFAULT_SPEED = 10;
const DEFAULT_AMPLITUDE = 2;
const DEFAULT_FREQUENCY = 0.5;
const DEFAULT_SCALE = 0.2;
const DEFAULT_BRIGHTNESS = 1.5;

function useAnimatedValue(initialValue: number) {
  const [value, setValue] = useState(initialValue);
  const valueRef = useRef({ current: initialValue });
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  const animateFn = useCallback(
    (targetValue: number | number[], options: gsap.TweenVars = { duration: 0.5, ease: 'power2.out' }) => {
      if (tweenRef.current) {
        tweenRef.current.kill();
      }

      if (Array.isArray(targetValue)) {
        // Handle pulse array (e.g. brightness [0.5, 2.5])
        tweenRef.current = gsap.fromTo(valueRef.current,
          { current: targetValue[0] },
          {
            current: targetValue[1],
            ...options,
            onUpdate: () => setValue(valueRef.current.current)
          }
        );
      } else {
        tweenRef.current = gsap.to(valueRef.current, {
          current: targetValue,
          ...options,
          onUpdate: () => setValue(valueRef.current.current)
        });
      }
    },
    []
  );

  return { value, animate: animateFn, tweenRef };
}

export function useAgentAudioVisualizerAura(
  state: AgentState | undefined,
  audioTrack?: LocalAudioTrack | RemoteAudioTrack | TrackReferenceOrPlaceholder,
) {
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  
  const { value: scale, animate: animateScale, tweenRef: scaleTweenRef } = useAnimatedValue(DEFAULT_SCALE);
  const { value: amplitude, animate: animateAmplitude } = useAnimatedValue(DEFAULT_AMPLITUDE);
  const { value: frequency, animate: animateFrequency } = useAnimatedValue(DEFAULT_FREQUENCY);
  const { value: brightness, animate: animateBrightness } = useAnimatedValue(DEFAULT_BRIGHTNESS);

  const volume = useTrackVolume(audioTrack as TrackReference, {
    fftSize: 512,
    smoothingTimeConstant: 0.55,
  });

  useEffect(() => {
    const defaultTransition = { duration: 0.5, ease: 'power2.out' };
    const pulseTransition = { duration: 0.35, ease: 'power2.out', repeat: -1, yoyo: true };
    const springTransition = { duration: 1.0, ease: 'elastic.out(1, 0.35)' };

    switch (state) {
      case 'idle':
      case 'failed':
      case 'disconnected':
        setSpeed(10);
        animateScale(0.2, defaultTransition);
        animateAmplitude(1.2, defaultTransition);
        animateFrequency(0.4, defaultTransition);
        animateBrightness(1.0, defaultTransition);
        return;
      case 'listening':
      case 'pre-connect-buffering':
        setSpeed(20);
        animateScale(0.3, springTransition);
        animateAmplitude(1.0, defaultTransition);
        animateFrequency(0.7, defaultTransition);
        animateBrightness([1.5, 2.0], pulseTransition);
        return;
      case 'thinking':
      case 'connecting':
      case 'initializing':
        setSpeed(30);
        animateScale(0.3, defaultTransition);
        animateAmplitude(0.5, defaultTransition);
        animateFrequency(1, defaultTransition);
        animateBrightness([0.5, 2.5], pulseTransition);
        return;
      case 'speaking':
        setSpeed(70);
        animateScale(0.3, defaultTransition);
        animateAmplitude(0.75, defaultTransition);
        animateFrequency(1.25, defaultTransition);
        animateBrightness(1.5, defaultTransition);
        return;
    }
  }, [state, animateScale, animateAmplitude, animateFrequency, animateBrightness]);

  useEffect(() => {
    if (state === 'speaking' && volume > 0 && (!scaleTweenRef.current || !scaleTweenRef.current.isActive())) {
      animateScale(0.2 + 0.2 * volume, { duration: 0 });
    }
  }, [
    state,
    volume,
    scaleTweenRef,
    animateScale,
    animateAmplitude,
    animateFrequency,
    animateBrightness,
  ]);

  return {
    speed,
    scale,
    amplitude,
    frequency,
    brightness,
  };
}
